import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { CalendarMinus } from "lucide-react";
import { PortalOccurrence } from "../../features/portal/models/PortalOccurrence";
import { BookableOccurrenceResponse } from "../../features/portal/contracts/BookableOccurrenceResponse";
import PortalSeatStatusChip from "./PortalSeatStatusChip";
import PortalCancelOccurrenceDialog from "./PortalCancelOccurrenceDialog";
import { formatTimeOfDay } from "../../features/utils/displayFormat";
import { formatMoney } from "../../features/billing/utils/money";
import { fallbackVisitStatusStyle, visitStatusStyles } from "./visitStatusStyles";
import { PortalContractResponse } from "../../features/portal/contracts/PortalContractResponse";
import { chargedVisitShape } from "./chargedVisitShape";

interface PortalScheduleListProps {
  occurrences: PortalOccurrence[] | undefined;
  /** The member (self or ward) the schedule belongs to; enables the Cancel action. */
  memberId?: string;
  /** Called after the member cancelled a date, so the caller can reload. */
  onCancelled?: () => void;
  /** The dates offered for booking; a waived date open there again carries the rebook hint. */
  bookable?: BookableOccurrenceResponse[];
  /** The member's contracts (from the balance), so a charged date can say what it cost them. */
  contracts?: PortalContractResponse[];
}

/** Whether the waived date is offered again: same Session, same date, no seat yet, not full. */
const isOpenAgain = (occurrence: PortalOccurrence, bookable: BookableOccurrenceResponse[] | undefined): boolean =>
  !occurrence.isCancelled &&
  occurrence.cancelledVisitStatus === "CancelledWaived" &&
  (bookable ?? []).some(
    (b) =>
      b.bookingId === occurrence.bookingId && b.date === occurrence.date && b.seatStatus === null && b.placesLeft !== 0,
  );

/**
 * The member's upcoming occurrences, grouped by date. A date with a Cancelled Visit shows its
 * recorded outcome with the visit history's labels (and the late-cancellation fee line when the
 * policy fee was charged, the amount billed when it was charged otherwise, else the pack visit it
 * used or the subscription fee that covers it); a waived date offered
 * for booking again says so. A date the school cancelled reads "Cancelled" and wins (PD-0024).
 */
const PortalScheduleList = ({ occurrences, memberId, onCancelled, bookable, contracts }: PortalScheduleListProps) => {
  const { t, i18n } = useTranslation();
  const [cancelling, setCancelling] = useState<PortalOccurrence | null>(null);

  if (!occurrences) {
    return <p className="text-sm text-grey-40 dark:text-grey-80">{t("common.loading", "Loading…")}</p>;
  }

  if (occurrences.length === 0) {
    return (
      <p className="text-sm text-grey-40 dark:text-grey-80">
        {t("portal.noUpcoming")}
      </p>
    );
  }

  const byDate = occurrences.reduce<Record<string, PortalOccurrence[]>>((groups, occurrence) => {
    (groups[occurrence.date] ??= []).push(occurrence);
    return groups;
  }, {});

  const formatDate = (date: string) =>
    new Intl.DateTimeFormat(i18n.language, {
      weekday: "short",
      day: "numeric",
      month: "long",
    }).format(new Date(`${date}T00:00:00`));

  return (
    <>
      <ul className="flex flex-col gap-3">
        {Object.entries(byDate).map(([date, items]) => (
          <li key={date}>
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-grey-40 dark:text-grey-80">
              {formatDate(date)}
            </p>
            <ul className="flex flex-col gap-2">
              {items.map((occurrence) => {
                const visitStatus = occurrence.cancelledVisitStatus;
                const isCancelled = occurrence.isCancelled || visitStatus !== null;
                const fee = occurrence.lateCancellationFee;
                const charge = fee ? null : occurrence.chargedAmount;
                const shape =
                  !occurrence.isCancelled && visitStatus === "CancelledCharged" && !fee && !charge
                    ? chargedVisitShape(occurrence.date, contracts)
                    : null;
                return (
                  <li
                    key={`${occurrence.bookingId}-${occurrence.date}-${occurrence.time}`}
                    className="with-border flex items-center justify-between rounded-2xl bg-white p-3 dark:bg-primary-20"
                  >
                    <div>
                      <p
                        className={
                          isCancelled
                            ? "font-medium text-grey-40 line-through dark:text-grey-80"
                            : "font-medium text-grey-20 dark:text-white"
                        }
                      >
                        {occurrence.title}
                      </p>
                      <p className={`text-sm text-grey-40 dark:text-grey-80 ${isCancelled ? "line-through" : ""}`}>
                        {formatTimeOfDay(occurrence.time, i18n.language)} · {occurrence.durationMinutes}{" "}
                        {t("portal.minutes", "min")}
                      </p>
                      {fee && (
                        <p className="text-sm text-grey-40 dark:text-grey-80">
                          {t("portal.lateCancellationFee", {
                            amount: formatMoney(fee.amountMinorUnits, fee.currency, i18n.language),
                          })}
                        </p>
                      )}
                      {charge && (
                        <p className="text-sm text-grey-40 dark:text-grey-80">
                          {t("portal.cancelledRow.chargedFull", {
                            amount: formatMoney(charge.amountMinorUnits, charge.currency, i18n.language),
                          })}
                        </p>
                      )}
                      {shape && (
                        <p className="text-sm text-grey-40 dark:text-grey-80">
                          {t(shape === "pack" ? "portal.cancelledRow.usedPackVisit" : "portal.cancelledRow.coveredBySubscription")}
                        </p>
                      )}
                      {isOpenAgain(occurrence, bookable) && (
                        <p className="text-sm text-grey-40 dark:text-grey-80">{t("portal.cancelledRow.rebookHint")}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {!isCancelled && occurrence.seatStatus && (
                        <PortalSeatStatusChip status={occurrence.seatStatus} />
                      )}
                      {memberId && occurrence.canCancel && !isCancelled && (
                        <button type="button" className="btn btn-secondary" onClick={() => setCancelling(occurrence)}>
                          <CalendarMinus size={18} />
                          {t("portal.cancel.action")}
                        </button>
                      )}
                      {occurrence.isCancelled ? (
                        <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800 dark:bg-red-900/30 dark:text-red-300">
                          {t("portal.cancelled", "Cancelled")}
                        </span>
                      ) : (
                        visitStatus && (
                          <span
                            className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                              visitStatusStyles[visitStatus] ?? fallbackVisitStatusStyle
                            }`}
                          >
                            {fee
                              ? t("portal.visitStatus.CancelledLateFee")
                              : t(`portal.visitStatus.${visitStatus}`)}
                          </span>
                        )
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </li>
        ))}
      </ul>
      {cancelling && memberId && (
        <PortalCancelOccurrenceDialog
          memberId={memberId}
          bookingId={cancelling.bookingId}
          date={cancelling.date}
          title={cancelling.title}
          dateLabel={formatDate(cancelling.date)}
          onClose={() => setCancelling(null)}
          onCancelled={() => {
            setCancelling(null);
            toast.success(t("portal.cancel.done"));
            onCancelled?.();
          }}
        />
      )}
    </>
  );
};

export default PortalScheduleList;
